/**
 * Constants for ExposureAlertsCard.
 *
 * Defines common ports and protocols for exposure alert configuration
 * with descriptions for better UX in autocomplete.
 */

import type { Suggestion } from '@/components/SearchableTagInput';

/**
 * Common ports for exposure alerts with service descriptions.
 * Ordered by typical usage frequency and security importance.
 */
export const PORT_SUGGESTIONS: Suggestion[] = [
  { value: '21', label: '21', description: 'FTP' },
  { value: '22', label: '22', description: 'SSH' },
  { value: '23', label: '23', description: 'Telnet' },
  { value: '25', label: '25', description: 'SMTP' },
  { value: '53', label: '53', description: 'DNS' },
  { value: '79', label: '79', description: 'Finger' },
  { value: '80', label: '80', description: 'HTTP' },
  { value: '110', label: '110', description: 'POP3' },
  { value: '111', label: '111', description: 'RPC' },
  { value: '135', label: '135', description: 'MSRPC' },
  { value: '139', label: '139', description: 'NetBIOS' },
  { value: '143', label: '143', description: 'IMAP' },
  { value: '389', label: '389', description: 'LDAP' },
  { value: '443', label: '443', description: 'HTTPS' },
  { value: '445', label: '445', description: 'SMB' },
  { value: '465', label: '465', description: 'SMTPS' },
  { value: '587', label: '587', description: 'Submission' },
  { value: '636', label: '636', description: 'LDAPS' },
  { value: '993', label: '993', description: 'IMAPS' },
  { value: '995', label: '995', description: 'POP3S' },
  { value: '1433', label: '1433', description: 'MSSQL' },
  { value: '1521', label: '1521', description: 'Oracle' },
  { value: '2049', label: '2049', description: 'NFS' },
  { value: '3306', label: '3306', description: 'MySQL' },
  { value: '3389', label: '3389', description: 'RDP' },
  { value: '5432', label: '5432', description: 'PostgreSQL' },
  { value: '5900', label: '5900', description: 'VNC' },
  { value: '5985', label: '5985', description: 'WinRM HTTP' },
  { value: '5986', label: '5986', description: 'WinRM HTTPS' },
  { value: '6379', label: '6379', description: 'Redis' },
  { value: '8080', label: '8080', description: 'HTTP Alt' },
  { value: '8443', label: '8443', description: 'HTTPS Alt' },
  { value: '9200', label: '9200', description: 'Elasticsearch' },
  { value: '27017', label: '27017', description: 'MongoDB' },
];

/**
 * Common protocols for exposure alerts with descriptions.
 * Ordered alphabetically for easy lookup.
 */
export const PROTOCOL_SUGGESTIONS: Suggestion[] = [
  { value: 'dns', label: 'dns', description: 'Domain Name System' },
  { value: 'ftp', label: 'ftp', description: 'File Transfer Protocol' },
  { value: 'http', label: 'http', description: 'Hypertext Transfer Protocol' },
  { value: 'https', label: 'https', description: 'HTTP Secure' },
  {
    value: 'imap',
    label: 'imap',
    description: 'Internet Message Access Protocol',
  },
  { value: 'imaps', label: 'imaps', description: 'IMAP Secure' },
  { value: 'jdwp', label: 'jdwp', description: 'Java Debug Wire Protocol' },
  {
    value: 'ldap',
    label: 'ldap',
    description: 'Lightweight Directory Access Protocol',
  },
  { value: 'ldaps', label: 'ldaps', description: 'LDAP Secure' },
  { value: 'mssql', label: 'mssql', description: 'Microsoft SQL Server' },
  { value: 'mysql', label: 'mysql', description: 'MySQL Database' },
  { value: 'nfs', label: 'nfs', description: 'Network File System' },
  { value: 'oracle', label: 'oracle', description: 'Oracle Database' },
  { value: 'pop3', label: 'pop3', description: 'Post Office Protocol v3' },
  { value: 'pop3s', label: 'pop3s', description: 'POP3 Secure' },
  {
    value: 'postgresql',
    label: 'postgresql',
    description: 'PostgreSQL Database',
  },
  { value: 'rdp', label: 'rdp', description: 'Remote Desktop Protocol' },
  { value: 'redis', label: 'redis', description: 'Redis In-Memory Store' },
  { value: 'smb', label: 'smb', description: 'Server Message Block' },
  {
    value: 'smtp',
    label: 'smtp',
    description: 'Simple Mail Transfer Protocol',
  },
  { value: 'smtps', label: 'smtps', description: 'SMTP Secure' },
  { value: 'ssh', label: 'ssh', description: 'Secure Shell' },
  { value: 'telnet', label: 'telnet', description: 'Teletype Network' },
  { value: 'vnc', label: 'vnc', description: 'Virtual Network Computing' },
  { value: 'winrm', label: 'winrm', description: 'Windows Remote Management' },
];
